import React, { PropsWithChildren } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { UserMenu } from './UserMenu';
import { Slide, useScrollTrigger } from '@mui/material';
import { useAppSelector } from '@/hooks';
import { AppBarStyle, selectAppBarStyle } from '@/modules/ui';
import { ContextActions } from '@/components/contextActions/ContextActions';
import { NavToggle } from './NavToggle';
import { ActiveHospitalSelect } from './ActiveHospitalSelect';
import { LayoutGroup, motion } from 'framer-motion';

interface StyledAppBarProps extends MuiAppBarProps {
  open: boolean;
  drawerWidth: number;
}

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth'
})<StyledAppBarProps>(({ theme, open, drawerWidth }) => ({
  overflow: 'hidden',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const HideOnScroll = ({ children }: PropsWithChildren) => {
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children as React.ReactElement}
    </Slide>
  );
};

const AppBarContainer = ({
  type,
  children
}: PropsWithChildren<{ type: AppBarStyle }>) => {
  if (type === 'fixed') {
    return <>{children}</>;
  }

  return <HideOnScroll>{children}</HideOnScroll>;
};

interface AppBarProps extends StyledAppBarProps {
  onToggleClick: () => void;
}
export function AppBar({ onToggleClick, ...props }: AppBarProps) {
  const appBarStyle = useAppSelector(selectAppBarStyle);

  return (
    <AppBarContainer type={appBarStyle}>
      <StyledAppBar
        elevation={0}
        position="fixed"
        color="transparent"
        {...props}
      >
        <Toolbar>
          <Box mr={2}>
            <NavToggle onClick={onToggleClick} />
          </Box>
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <LayoutGroup>
              <Box layout component={motion.div} flexGrow={1}>
                <ContextActions />
              </Box>
              <motion.div layout>
                <ActiveHospitalSelect />
              </motion.div>
              <motion.div layout>
                <UserMenu />
              </motion.div>
            </LayoutGroup>
          </Box>
        </Toolbar>
      </StyledAppBar>
    </AppBarContainer>
  );
}
